<template>
  <v-row
    style="height: 100%"
    class="ml-3"
    v-disable-all="hasReadOnlyAccess"
  >
    <v-col v-if="value == null" justify="center">
      <v-btn elevation="2" @click="addTargetWeight()" class="mt-2">
        <v-icon left>mdi-plus-circle-outline</v-icon>
        {{ $t("journeyOutcomeGoals.addTargetWeight") }}
      </v-btn>
    </v-col>
    <v-col v-if="value != null" cols="12">
      <v-form ref="form" v-model="valid">
        <v-row justify="center" align="center" style="height: 100%">
          <v-col cols="3" justify="center">
            <p class="mb-0">{{ $t("dummy.outcomeGoals.targetWeight") }}:</p>
          </v-col>
          <v-col cols="2">
            <v-text-field
              class="pt-6"
              v-model="value"
              outlined
              :rules="targetWeightRules"
              dense
              @keypress="onKeyPress"
              @paste="onPaste"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              :items="[kilogramUnit.unit]"
              v-model="valueUnitSelect"
              disabled
              hide-details
              dense
              class="mt-0 pt-0"
            ></v-select>
          </v-col>
          <v-col cols="5" justify="center">
            <v-btn
              v-if="value !== targetWeightValue"
              class="green mr-2"
              color="primary"
              elevation="2"
              @click="saveTargetWeight(value)"
              :disabled="!valid"
            >
              <v-icon left>mdi-content-save-outline</v-icon>

              {{ $ucfirst($t('buttons.save', {catalog: 'app_mobile'})) }}
            </v-btn>
            <v-btn color="error" elevation="2" @click="saveTargetWeight(null)">
              <v-icon left>mdi-close-circle-outline</v-icon>

              {{ $t("journeyOutcomeGoals.removeTargetWeight") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import {UCUMUnit} from "../../../../helper/UCUMUnit";

/**
 * Component GoalSettingOutcomeGoalRowTargetWeight.vue
 *
 * @module GoalSettingOutcomeGoalRowTargetWeight
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.5] app/src/APP_DASHBOARD/templates/components/Parts/GoalSettingOutcomeGoalRowTargetWeight.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "GoalSettingOutcomeGoalRowTargetWeight",
  props: {
    extraInformation: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters({
      patientId: "patientSummary/id",
      outcomeComponents: "patientSummary/outcomeComponents",
      targetWeightValueCopy: "patientSummary/targetWeightValue",
      targetWeightUnit: "patientSummary/targetWeightUnit",
      length: "patientSummary/length",
      hasReadOnlyAccess: "user/hasReadOnlyAccess"
    }),
    kilogramUnit: {
      get() {
        return UCUMUnit.KILOGRAM;
      }
    }
  },
  created() {},
  data: function () {
    return {
      statusClass: "null",
      value: null,
      targetWeightValue: null,
      valueUnitSelect: UCUMUnit.KILOGRAM.unit,
      valid: true,
      targetWeightRules: [
        (v) => !isNaN(v) || this.$t("dummy.general.invalid_number", {catalog: 'validators'}),
        (v) => !!v.toString() || this.$t("reportParametersHomeScreen.cannotSaveEmptyRecord", {catalog: 'app_mobile'}),
        (v) => this.valueInRange(v) || this.$t("reportParametersHomeScreen.errorWeightUnrealisticNumber", {catalog: 'app_mobile'}),
      ],
    };
  },
  watch: {
    value(curr) {
      if (curr !== this.targetWeightValue) {
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      }
    }
  },
  methods: {
    updateValues() {
      if (this.targetWeightValue == null) {
        this.value = this.targetWeightValueCopy || null;
        this.targetWeightValue = this.targetWeightValueCopy || null;
      } else {
        this.value = null;
      }
    },
    addTargetWeight() {
      let _value = 0;
      if (this.extraInformation.BMI > 25) {
        _value =
          this.extraInformation.weightKg -
          this.extraInformation.weightKg * 0.05;
      } else {
        _value = this.extraInformation.weightKg;
      }
      // limit to 1 decimal
      if (_value && this.getDecimalCount(_value) > 1) {
        _value = _value.toFixed(1);
      }
      this.value = _value || ''
      if (_value !== null) {
        this.saveTargetWeight(_value, true);
      }
    },
    saveTargetWeight(value) {
      this.$app.apolloClient
        .mutate({
          mutation: gql`
            mutation createUpdatePatient(
              $patientId: Int!
              $targetWeightUnit: String
              $targetWeightValue: Float
            ) {
              createUpdatePatient(
                patientId: $patientId
                targetWeightUnit: $targetWeightUnit
                targetWeightValue: $targetWeightValue
              ) {
                user {
                  patient {
                    targetWeightValue
                    targetWeightUnit
                  }
                }
              }
            }
          `,
          variables: {
            patientId: parseInt(this.patientId),
            targetWeightUnit: this.valueUnitSelect,
            targetWeightValue: parseFloat(value),
          },
        })
        .then((result) => {
          const data = result.data?.createUpdatePatient?.user?.patient;
          if (data) {
            this.value = data.targetWeightValue || null;
            this.targetWeightValue = data.targetWeightValue || null;
            this.$store.dispatch('patientSummary/updateTargetWeight', data);
          }

          this.$store.dispatch('patientSummary/toggleUnsavedChanges', false);
        });
    },
    onKeyPress(event) {
      const value = event.target.value;
      const count = this.getDecimalCount(value)
      if (count >= 1) {
        event.preventDefault();
      }
    },
    onPaste(event) {
      const pasted = event.clipboardData.getData('text/plain')
      const replaced = pasted.replaceAll(',','.');
      const parsed = parseFloat(replaced);
      const count = this.getDecimalCount(parsed)
      event.preventDefault();

      if (!isNaN(parsed) && !(parsed < 0)) {
        count > 1
          ? this.value = parsed.toFixed(1)
          : this.value = parsed;
      }
    },
    getDecimalCount(number) {
      if (!number) return;
      const numStr = number.toString();
      let length = 0

      if (numStr.includes('.')) {
        length = numStr.split('.')[1].length;
      } else if (numStr.includes(',')) {
        length = numStr.split(',')[1].length;
      }
      return length;
    },
    valueInRange(value) {
      const val = parseFloat(value);
      const length = parseFloat(this.length) / 100;
      const min = 12 * length * length;
      const max = 60 * length * length;
      return val >= min && val <= max
    }
  },
  mounted: function () {
    this.updateValues();
  },
  components: {},
};
</script>

<style lang="scss">
</style>
